import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  // FELLHEIMAT (01.11.2026): Knopf-Leiste VOR dem echten ATC.
  // Die Horizon-Leiste oben erscheint erst, wenn der echte ATC nach oben aus
  // dem Bild ist. Solange er noch UNTER dem Bild liegt (beim Laden, beim
  // Runterscrollen), zeigt diese Datei unten nur einen Knopf im Stil des
  // echten ATC. Klick = Klick auf den echten ATC, keine eigene Warenkorb-
  // Logik; Mengenstaffel, Groesse, Gratis-Positionen und Paketschutz laufen
  // so exakt wie beim normalen Kauf. Ist eine Auswahl offen, scrollt der
  // Knopf dorthin. Beide Leisten weichen jedem offenen <dialog> (Warenkorb,
  // Pop-ups, Groessenrechner).

  /** @type {IntersectionObserver | null} */
  #preObserver = null;

  /** @type {MutationObserver | null} */
  #dialogObserver = null;

  /** @type {HTMLElement | null} */
  #preBar = null;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();
    this.#setupPreBar();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    // IntersectionObserver callbacks gate visibility on #isChatActive(), but
    // if the shopper scrolls before the Inbox bundle has upgraded
    // <shopify-chat>, the bar shows and nothing re-runs that check. Hide it
    // once the element is defined so the bar doesn't overlap the chat UI.
    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;
      if (this.#isStuck && this.#isChatActive()) this.#hideStickyBar();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#preObserver?.disconnect();
    this.#dialogObserver?.disconnect();
    this.#preBar?.remove();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            if (!this.#isChatActive()) {
              this.#showStickyBar();
            }
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  /**
   * FELLHEIMAT: baut die Knopf-Leiste und beobachtet den echten ATC.
   * Eigener Observer mit -96px unten: Die Leiste deckt den unteren Rand ab,
   * der echte ATC soll erst als "sichtbar" zaehlen, wenn er darueber steht.
   * Sonst stuenden kurz zwei Knoepfe uebereinander.
   */
  #setupPreBar() {
    const productForm = this.#getProductForm();
    const realButton = productForm?.querySelector('[ref="addToCartButton"]');
    const buyButtonsBlock = productForm?.closest('.buy-buttons-block');
    if (!realButton || !buyButtonsBlock) return;

    fhInjectPreBarStyles();

    const bar = document.createElement('div');
    bar.className = 'fh-pre-atc';
    bar.dataset.visible = 'false';
    bar.setAttribute('role', 'region');
    bar.setAttribute('aria-label', 'In den Warenkorb');
    // Im Element selbst, damit Farbschema und Block-Stil der Section greifen.
    this.appendChild(bar);
    this.#preBar = bar;
    this.#syncPreButton();


    const { signal } = this.#abortController;
    bar.addEventListener('click', this.#handlePreClick, { signal });
    // Variantenwechsel tauscht den echten Knopf aus (z.B. "Ausverkauft").
    this.closest('.shopify-section')?.addEventListener(
      StandardEvents.productSelect,
      (event) => event.promise?.then(() => setTimeout(() => this.#syncPreButton())).catch(() => {}),
      { signal }
    );

    // Der echte ATC zaehlt als sichtbar, sobald seine Oberkante ueber der
    // Oberkante der Knopf-Leiste steht. offsetTop einer fixierten Leiste ist
    // ihre Lage im Bild ohne die Einblend-Verschiebung - so stimmt die Grenze
    // auch mit iPhone-Abstand unten und wechselnder Safari-Leiste.
    const evaluate = () => {
      const barTop = bar.offsetTop || window.innerHeight - 96;
      // Exakt deckungsgleich wechseln - dann ist der Tausch unsichtbar.
      const limit = barTop - 0.5;
      // Knopf jedes Mal neu holen - Variantenwechsel tauscht ihn aus.
      const real = productForm.querySelector('[ref="addToCartButton"]') ?? buyButtonsBlock;
      const realRect = real.getBoundingClientRect();
      const below = realRect.top > limit;
      if (below && bar.dataset.visible !== 'true') this.#syncPreButton();
      bar.dataset.visible = below && !this.#isChatActive() ? 'true' : 'false';

      // Uebergabe an den echten Knopf, an die Scrollposition gekoppelt: Ab dem
      // Moment, in dem der echte Knopf unten ins Bild kommt, liegt er HINTER
      // dem Sticky-Knopf. Auf dem Weg bis zur gleichen Oberkante zieht sich der
      // Sticky auf die Breite des echten Knopfs und verliert seinen Schatten.
      // Bei gleicher Oberkante sind beide deckungsgleich, der Wechsel ist
      // unsichtbar. Bewusst KEIN Ausblenden: Halbdurchsichtig schimmerte der
      // versetzte echte Knopf durch - doppelte Schrift.
      // Weg der Animation: 260px Scrollen, beginnt also schon, waehrend der
      // echte Knopf noch unter dem Bild liegt. Nur der sichtbare Rest (~64px)
      // war bei normalem Tempo 2-3 Bilder - wirkte wie ein Ruck.
      // Smoothstep: startet und endet sanft statt linear.
      const t = Math.min(Math.max(1 - (realRect.top - barTop) / 260, 0), 1);
      const progress = t * t * (3 - 2 * t);
      const grow = Math.max((realRect.width - bar.offsetWidth) / 2, 0) * progress;
      // Nur schreiben, wenn sich etwas aendert - sonst Stil-Neuberechnung bei
      // jedem Scroll-Frame, auch weit weg vom Knopf.
      const growValue = `${grow.toFixed(1)}px`;
      if (bar.style.getPropertyValue('--fh-pre-grow') !== growValue) {
        bar.style.setProperty('--fh-pre-fade', (1 - progress).toFixed(3));
        bar.style.setProperty('--fh-pre-grow', growValue);
      }
      // Unter dem Bild gehoert der Platz der Knopf-Leiste. Die Horizon-Leiste
      // blendet sich nur aus, wenn der ATC sichtbar WIRD - bei einem Sprung
      // von unten nach ganz oben (Anker, schnelles Wischen) bliebe sie stehen.
      if (below && this.#isStuck) this.#hideStickyBar();
    };

    this.#preObserver = new IntersectionObserver(evaluate, { rootMargin: '0px 0px -96px 0px' });
    this.#preObserver.observe(buyButtonsBlock);

    // Der Observer meldet sich nur beim Wechsel sichtbar/unsichtbar. Ein
    // Sprung von "drueber" nach "drunter" ist kein Wechsel, daher zusaetzlich
    // einmal pro Frame beim Scrollen nachsehen.
    let frame = 0;
    window.addEventListener(
      'scroll',
      () => {
        if (frame) return;
        frame = requestAnimationFrame(() => {
          frame = 0;
          evaluate();
        });
      },
      { passive: true, signal: this.#abortController.signal }
    );

    // Offene Pop-ups und Drawer sind <dialog open>. Menues (Hauptmenue,
    // Untermenues, Sprach-/Laenderwahl) oeffnet Horizon dagegen als
    // <details open> - aber nur die im Header bzw. mit Menue-Klassen zaehlen,
    // FAQ-Akkordeons im Inhalt sind auch <details> und sollen nichts ausblenden.
    const menuScope = 'header, .header, [class*="menu-drawer"], [class*="dropdown"], [class*="localization"]';
    const update = () => {
      const dialogOpen = Array.from(document.querySelectorAll('dialog[open]')).some(
        (dialog) => dialog.getClientRects().length > 0
      );
      const menuOpen = Array.from(document.querySelectorAll('details[open]')).some((details) =>
        details.closest(menuScope)
      );
      this.toggleAttribute('data-fh-covered', dialogOpen || menuOpen);
    };
    this.#dialogObserver = new MutationObserver(update);
    this.#dialogObserver.observe(document.body, { subtree: true, attributes: true, attributeFilter: ['open'] });
    update();
  }

  /**
   * FELLHEIMAT: Kopie des echten ATC in die Leiste - gleiche Klassen, gleicher
   * Text (auch "Ausverkauft" und Mengenzusatz), aber ohne id/name/ref, damit
   * kein Skript die Kopie fuer den echten Knopf haelt.
   */
  #syncPreButton() {
    const real = this.#getProductForm()?.querySelector('[ref="addToCartButton"]');
    if (!real || !this.#preBar) return;
    const copy = /** @type {HTMLButtonElement} */ (real.cloneNode(true));
    for (const attr of ['id', 'name', 'ref', 'on:click', 'data-testid', 'data-puppet', 'form']) copy.removeAttribute(attr);
    for (const el of copy.querySelectorAll('[ref], [id]')) {
      el.removeAttribute('ref');
      el.removeAttribute('id');
    }
    copy.type = 'button';
    copy.disabled = false;
    copy.removeAttribute('aria-disabled');
    copy.classList.add('fh-pre-atc__button');
    copy.dataset.soldOut = real.disabled ? 'true' : 'false';
    this.#preBar.replaceChildren(copy);
    // Knopf ist absolut positioniert (Safari-Toenung), der Rahmen braucht
    // daher die Hoehe des echten Knopfs ausdruecklich.
    if (real.offsetHeight) this.#preBar.style.setProperty('--fh-pre-h', `${real.offsetHeight}px`);
  }

  /**
   * FELLHEIMAT: Klick auf die Knopf-Leiste.
   * @param {MouseEvent} event
   */
  #handlePreClick = (event) => {
    if (!(event.target instanceof Element) || !event.target.closest('.fh-pre-atc__button')) return;
    const productForm = this.#getProductForm();
    const real = /** @type {HTMLButtonElement | null} */ (productForm?.querySelector('[ref="addToCartButton"]'));
    if (!productForm || !real) return;

    const missing = this.#findMissingSelection(productForm);
    if (missing || real.disabled) {
      this.#scrollToSelection(missing ?? document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`) ?? real);
      return;
    }
    real.click();
  };

  /**
   * FELLHEIMAT: erste offene Pflichtauswahl - Optionsgruppe ohne Haken,
   * leeres Auswahlfeld oder ein ungueltiges Pflichtfeld im Formular
   * (z.B. Groessen der Zusatzjacken).
   * @param {HTMLElement} productForm
   * @returns {Element | null}
   */
  #findMissingSelection(productForm) {
    const picker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (picker) {
      for (const group of picker.querySelectorAll('fieldset')) {
        if (group.querySelector('input[type="radio"]') && !group.querySelector('input[type="radio"]:checked')) {
          return group;
        }
      }
      for (const select of picker.querySelectorAll('select')) {
        if (!select.value) return select.closest('.variant-option') ?? select;
      }
    }
    const form = productForm.querySelector('form');
    const invalid = form?.querySelector(':invalid:not(form):not(fieldset)');
    return invalid ? invalid.closest('fieldset') ?? invalid : null;
  }

  /**
   * @param {Element} target
   */
  #scrollToSelection(target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.classList.add('fh-pre-atc-need');
    setTimeout(() => target.classList.remove('fh-pre-atc-need'), 1800);
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.addToCartButton || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([this.refs.addToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Checks whether the Shopify Chat is active on the page.
   * When active, the sticky bar must stay hidden to avoid overlapping the chat UI.
   *
   * <shopify-chat> is rendered unconditionally by chat-drawer.liquid, but
   * the "Ask anything" button only paints once the Inbox app has installed
   * and upgraded the element. Gate on the registration of the custom element
   * (the same signal chat-drawer.liquid uses via customElements.whenDefined)
   * so the inert placeholder on shops without Inbox doesn't suppress the
   * sticky bar.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (!customElements.get('shopify-chat')) return false;
    return Boolean(document.querySelector('shopify-chat'));
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

/**
 * FELLHEIMAT: Stil der Knopf-Leiste. Hier statt in einer neuen Theme-Datei:
 * neue Snippets mit {% stylesheet %} verschieben die compiled_assets-Liste.
 * Der Knopf selbst bringt seine Optik ueber die kopierten Klassen mit.
 */
function fhInjectPreBarStyles() {
  if (document.getElementById('fh-pre-atc-style')) return;
  const style = document.createElement('style');
  style.id = 'fh-pre-atc-style';
  style.textContent = `
    .fh-pre-atc {
      position: fixed;
      /* Safari 26 (iOS) prueft einen Punkt knapp ueber seiner Leiste, sucht
         das naechste fixierte Element und macht die Leiste undurchsichtig,
         wenn dieses >= 90 % der Breite hat. Mit 100 % breitem Rahmen kam
         beim Hochscrollen (Leiste faehrt aus) ein Rand - erst braun, mit
         Farbstreifen dann creme. Deshalb ist der FIXIERTE Rahmen hier nur
         84 % breit, mittig, und der Knopf fuellt ihn absolut aus.
         Nur der Knopf, mittig - kein Streifen, kein Bild, kein Preis.
         Die Leiste selbst laesst Klicks durch, nur der Knopf faengt sie. */
      bottom: calc(12px + env(safe-area-inset-bottom, 0px));
      left: 0;
      right: 0;
      width: min(84%, 520px);
      margin-inline: auto;
      z-index: 20;
      height: var(--fh-pre-h, 52px);
      pointer-events: none;
      opacity: 0;
      transform: translateY(16px);
      visibility: hidden;
    }
    .fh-pre-atc[data-visible='true'] {
      opacity: 1;
      transform: none;
      visibility: visible;
    }
    /* Nur das Einblenden ist weich. Ausblenden sofort - sonst stehen beim
       Hochwischen kurz zwei Knoepfe uebereinander (Transition des Zielzustands
       gilt, der versteckte Zustand hat keine). */
    @media (prefers-reduced-motion: no-preference) {
      .fh-pre-atc[data-visible='true'] {
        transition: opacity 0.2s ease, transform 0.25s ease;
      }
    }
    .fh-pre-atc .fh-pre-atc__button {
      position: absolute;
      /* Waechst bei der Uebergabe ueber den Rahmen hinaus auf die Breite des
         echten Knopfs - der FIXIERTE Rahmen bleibt dabei 84 % (Safari). */
      inset-inline: calc(var(--fh-pre-grow, 0px) * -1);
      bottom: 0;
      display: flex;
      width: auto;
      pointer-events: auto;
      box-shadow: 0 6px 20px rgb(74 55 40 / calc(0.28 * var(--fh-pre-fade, 1)));
    }
    .fh-pre-atc .fh-pre-atc__button[data-sold-out='true'] {
      opacity: 0.6;
    }
    .fh-pre-atc .fh-pre-atc__button .add-to-cart__added {
      display: none;
    }
    sticky-add-to-cart[data-fh-covered] .fh-pre-atc,
    sticky-add-to-cart[data-fh-covered] .sticky-add-to-cart__bar {
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
    .fh-pre-atc-need {
      outline: 2px solid currentColor;
      outline-offset: 6px;
      border-radius: 8px;
    }
  `;
  document.head.appendChild(style);
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
